import { getPropertyTypesApiClient } from "./../../../../../shared/apis/property-search/api-factories";
import { useQuery } from "@tanstack/react-query";
import { extractQueryState } from "../../helpers/extract-query-state";
import { PropertyTypeTreeDto } from "@shared/apis/property-search/generated";

const GET_CMA_QUERY_KEY = "get-property-types-tree";
const CACHE_TIME_IN_MS = 1000 * 60 * 10; // 10 minutes

// Dynamic per-MLS property types are disabled for now; always request canonical types.
const CANONICAL_PROPERTY_TYPES = true;

export const buildGetPropertyTypesQueryKey = (params: {
  mlsSources: string[];
  canonical: boolean;
}) => {
  const { mlsSources, canonical } = params;
  return [GET_CMA_QUERY_KEY, ...mlsSources, { canonical }];
};

export function useGetPropertyTypes(props: {
  mlsSources: string[];
  onSuccess?: (data: PropertyTypeTreeDto) => void;
}) {
  const { mlsSources, onSuccess } = props;
  const api = getPropertyTypesApiClient();

  const query = useQuery({
    queryKey: buildGetPropertyTypesQueryKey({
      mlsSources,
      canonical: CANONICAL_PROPERTY_TYPES,
    }),
    queryFn: async () => {
      const response = await api.propertyTypeV2ControllerGetTree({
        canonical: CANONICAL_PROPERTY_TYPES,
        mlsSources,
      });
      if (onSuccess) {
        onSuccess(response);
      }
      return orderPropertyTypes(response.propertyTypes);
    },
    refetchOnWindowFocus: false,
    gcTime: CACHE_TIME_IN_MS,
    staleTime: CACHE_TIME_IN_MS,
  });

  return extractQueryState(query);
}

const orderPropertyTypes = (
  propertyTypes: PropertyTypeTreeDto["propertyTypes"]
): PropertyTypeTreeDto["propertyTypes"] => {
  const order = [
    "Residential",
    "Residential Income",
    "Commercial",
    "Land",
    "Business Opportunity",
  ];

  return propertyTypes.sort((a, b) => {
    const aIndex = order.indexOf(a.name);
    const bIndex = order.indexOf(b.name);
    return aIndex - bIndex;
  });
};
